import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import {
  OrganizationUsersResult,
  AdminOrganizationUser,
} from 'src/app/admin/models/organization.model';
import { catchError } from 'rxjs/operators';
import {
  OrganizationUserApiError,
  OrganizationUserApiErrorTypes,
} from './organization-user.errors';

const BASE_URL = `${environment.apiOrg.url}/admin/users`;

@Injectable()
export class OrganizationUserService {
  constructor(private http: HttpClient) {}

  loadOrganizationUsers(organizationId: number): Observable<OrganizationUsersResult> {
    const url = BASE_URL;
    const params = {
      organizationId: organizationId.toString(),
    };

    return this.http
      .get<OrganizationUsersResult>(url, { params })
      .pipe(
        catchError((err) =>
          throwError(
            new OrganizationUserApiError(
              OrganizationUserApiErrorTypes.ServiceUnavailableError,
              err,
            ),
          ),
        ),
      );
  }

  addOrganizationUser(user) {
    return this.http.post<AdminOrganizationUser>(BASE_URL, user).pipe(
      catchError((err) => {
        if (err.status === 400) {
          return throwError(
            new OrganizationUserApiError(OrganizationUserApiErrorTypes.BadRequestError, err),
          );
        }

        if (err.status === 409) {
          return throwError(
            new OrganizationUserApiError(OrganizationUserApiErrorTypes.ConflictError, err),
          );
        }

        return throwError(
          new OrganizationUserApiError(OrganizationUserApiErrorTypes.ServiceUnavailableError, err),
        );
      }),
    );
  }

  updateOrganizationUser(id: string, user: Partial<AdminOrganizationUser>) {
    const url = `${BASE_URL}/${id}`;
    const params = user;

    return this.http.patch<AdminOrganizationUser>(url, params).pipe(
      catchError((err) => {
        if (err.status === 400) {
          return throwError(
            new OrganizationUserApiError(OrganizationUserApiErrorTypes.BadRequestError, err),
          );
        }

        return throwError(
          new OrganizationUserApiError(OrganizationUserApiErrorTypes.ServiceUnavailableError, err),
        );
      }),
    );
  }

  /**
   * Show the disable user confirmation dialog, and await a response
   */
  showDisableOrganizationUserConfirm(user: AdminOrganizationUser) {
    if (confirm(`Are you sure you want to disable "${user.name}" from this organization?`)) {
      return of(true);
    }

    return of(false);
  }

  /**
   * Removes the user from the organization
   */
  // TODO - retire legacy code STUD-2700
  removeOrganizationUserLegacy(userId: string, organizationId: number) {
    const url = `${BASE_URL}/legacy/${userId}?organizationId=${organizationId}`;
    return this.http.delete(url);
  }

  /**
   * Removes the user from the organization
   */
  removeOrganizationUser(userId: string, organizationId: number) {
    const url = `${environment.apiOrg.url}/admin/organizations/${organizationId}/users/${userId}`;
    return this.http.delete(url);
  }
}
